/**
 * json.tarkov.dev をブラウザから直接読むための小物。
 *
 * **ゲーム内テキストは Battlestate Games の著作物で、こちらから再配布できない。**
 * だからビルドに同梱せず、各ブラウザが自分で取りに行く。
 * → doc/service/master.md#タスクデータの取得
 *
 * レスポンスは必ず `{ data, translations }` の形。data の中の文字列は「翻訳キー」で、
 * translations に「どのフィールドが翻訳対象か」を示す JSONPath の一覧が入っている。
 * 実際の訳文は別ファイル（例: regular/tasks_ja）にフラットな辞書で入っている。
 *
 * `scripts/lib/tarkov.js` の移植。**同じ規則で動かす**（あちらは開発用スクリプト側）。
 */

import type { Lang } from './types'
import { IS_ACTIVITY, PROXY_TD } from './discord'

/**
 * Discord Activity の中では CSP が自分のプロキシドメインに閉じるため、
 * json.tarkov.dev を直接は叩けない。**URL Mapping 越しの相対パスにする。**
 * → doc/service/master.md#phase-2-discord-activity
 */
export const BASE = IS_ACTIVITY ? PROXY_TD : 'https://json.tarkov.dev'

export type Payload = { data: any; translations?: string[] }

export async function fetchJson(name: string, signal?: AbortSignal): Promise<Payload> {
  const res = await fetch(`${BASE}/${name}`, { headers: { Accept: 'application/json' }, signal })
  if (!res.ok) throw new Error(`HTTP ${res.status} — ${name}`)
  return (await res.json()) as Payload
}

/* ---- JSONPath ----
 * 出てくるのは以下の形だけなので、汎用実装は持たない（依存を増やさない）。
 *   $.data.tasks.*.name
 *   $.data.tasks.*.objectives[*].description
 *   $.data.tasks.*.objectives[*]..bodyParts[*]
 *   $.data.tasks.*.objectives[*]['healthEffect','playerHealthEffect'].effects[*]
 */

type Step =
  | { kind: 'key'; key: string }
  | { kind: 'descend'; key: string }
  | { kind: 'union'; keys: string[] }
  | { kind: 'all' }

const STEP = /\.\.([A-Za-z_$][\w$]*)|\.\*|\[\*\]|\.([A-Za-z_$][\w$]*)|\[((?:\s*'[^']*'\s*,?)+)\]/g

/** JSONPath 文字列を手順の配列にする。解釈できない形は投げる（黙って無視しない） */
export function parsePath(jsonPath: string): Step[] {
  const steps: Step[] = []
  let pos = jsonPath.startsWith('$') ? 1 : 0
  STEP.lastIndex = pos
  let m: RegExpExecArray | null
  while ((m = STEP.exec(jsonPath))) {
    if (m.index !== pos) break
    if (m[1] !== undefined) steps.push({ kind: 'descend', key: m[1] })
    else if (m[2] !== undefined) steps.push({ kind: 'key', key: m[2] })
    else if (m[3] !== undefined) steps.push({ kind: 'union', keys: [...m[3].matchAll(/'([^']*)'/g)].map((x) => x[1]) })
    else steps.push({ kind: 'all' })
    pos = STEP.lastIndex
  }
  if (pos !== jsonPath.length) throw new Error(`解釈できない JSONPath: ${jsonPath} (${pos} 文字目から)`)
  return steps
}

const has = (node: any, key: string) => Object.prototype.hasOwnProperty.call(node, key)

/** JSONPath の指す文字列を fn の戻り値で置き換える。root は破壊的に書き換わる */
export function replaceAtPath(root: any, steps: Step[], fn: (v: string) => string | undefined) {
  const visit = (node: any, i: number) => {
    if (node === null || typeof node !== 'object') return
    const step = steps[i]
    const last = i === steps.length - 1
    const hit = (parent: any, key: string) => {
      if (!last) return visit(parent[key], i + 1)
      const value = parent[key]
      if (typeof value !== 'string') return
      const next = fn(value)
      if (next !== undefined) parent[key] = next
    }
    if (step.kind === 'key') {
      if (has(node, step.key)) hit(node, step.key)
    } else if (step.kind === 'union') {
      for (const key of step.keys) if (has(node, key)) hit(node, key)
    } else if (step.kind === 'all') {
      for (const key of Object.keys(node)) hit(node, key)
    } else {
      // descend: 自分を含む全ての子孫から step.key を持つものを探す
      const rec = (n: any) => {
        if (n === null || typeof n !== 'object') return
        if (has(n, step.key)) hit(n, step.key)
        for (const key of Object.keys(n)) if (key !== step.key) rec(n[key])
      }
      rec(node)
    }
  }
  visit(root, 0)
}

/** 翻訳キーの生値。24桁の16進 id、または それに " name" などが付いた形 */
const RAW_KEY = /^[0-9a-f]{24}(\s|$)/

export type Lookup = (key: string) => string | undefined

/**
 * 翻訳キー1つを引くだけの口。**payload 全体を書き換えたくないとき**に使う
 * （items は 16.7MB あり、要るのは 500件ほどの名前だけ）。
 *
 * dicts は優先順（例: [ja, en]）。訳が無ければ undefined。
 */
export function makeLookup(...dicts: (Payload | Record<string, string>)[]): Lookup {
  const tables = dicts.filter(Boolean).map((d: any) => d.data ?? d) as Record<string, string>[]
  return (value: string) => {
    for (const table of tables) {
      const hit = table[value]
      // 訳文のはずが翻訳キーのまま、という壊れたエントリが上流にある。
      // 訳が無いものとして扱い、次の辞書（en）に落とす
      if (hit !== undefined && !RAW_KEY.test(hit)) return hit
    }
    return undefined
  }
}

/** payload.translations が指すフィールドを訳文に差し替える（破壊的） */
export function applyTranslations(payload: Payload, ...dicts: Payload[]) {
  const lookup = makeLookup(...dicts)
  for (const jsonPath of payload.translations ?? []) replaceAtPath(payload, parsePath(jsonPath), lookup)
}

/**
 * その言語で引く辞書の名前を、優先順で返す（→ doc/i18n/master.md#ゲームデータの言語）。
 *
 * **en は原文なのでフォールバック先が要らない**（取得が1本減る）。
 * ja は訳の無いフィールドがあるので en に落とす。
 */
export const dictNames = (name: string, lang: Lang): string[] =>
  lang === 'en' ? [`${name}_en`] : [`${name}_${lang}`, `${name}_en`]

/** 本体とその言語の辞書をまとめて取り、翻訳を当てた data を返す */
export async function loadTranslated(name: string, lang: Lang, signal?: AbortSignal): Promise<any> {
  const [base, ...dicts] = await Promise.all([
    fetchJson(name, signal),
    ...dictNames(name, lang).map((d) => fetchJson(d, signal)),
  ])
  applyTranslations(base, ...dicts)
  return base.data
}
